import {mapActions, mapGetters} from "vuex";
import { debounce } from 'lodash';
import { getStudentsTranslation } from "@/helpers/students-translations";

const DefaultAvatar = require('@/assets/img/default-avatar.jpg');

export default {
	name: 'course-sidebar-all-students',
	
	data() {
		return {
			currentCourseId		: 0,
			currentPage			: 1,
			itemsPerPage		: 25,
			searchQuery			: '',
			sortBy				: 'points_desc',
			sortDirection		: 'asc',
			allStudentsList		: [],
			totalStudents		: 0,
			isSortDropdownOpen	: false,
			sortOptions			: [
				{
					label: 'Бали (високі → низькі)',
					value: 'points_desc'
				},
				{
					label: 'Бали (низькі → високі)',
					value: 'points_asc'
				}
			]
		}
	},
	
	created() {
		// Initialize debounced search
		this.debouncedSearch = debounce(this.performSearch, 300);
		
		if (this.currentCourse) {
			this.currentCourseId = this.currentCourse.id;
			
			this.fetchList({
				id: this.currentCourse.id
			})
		}else{
			this.currentCourseId = 0;
			
			this.fetchList({
				id: 0
			})
		}
	},
	
	beforeDestroy() {
		// Cleanup
		if (this.debouncedSearch) {
			this.debouncedSearch.cancel();
		}
	},
	
	computed: {
		...mapGetters({
			listAllStudents: 'coursesWidgets/listAllStudents',
			currentCourse: 'courses/currentItem',
			// Use specific loading state for all students
			isListStudentsLoading: 'coursesWidgets/isAllStudentsLoading'
		}),
		
		// Filter and sort students based on search query and sort criteria
		filteredStudents() {
			let filtered = [...this.allStudentsList];
			
			if (this.searchQuery.trim()) {
				const query = this.searchQuery.toLowerCase().trim();
				filtered = filtered.filter(student => 
					student.fullName.toLowerCase().includes(query) ||
					student.countryTitle.toLowerCase().includes(query)
				);
			}

			// Apply sorting
			filtered.sort((a, b) => {
				let aValue, bValue;
				
				if (this.sortBy === 'points_desc' || this.sortBy === 'points_asc') {
					aValue = Number(a.scoredPoints) || 0;
					bValue = Number(b.scoredPoints) || 0;
					
					if (this.sortBy === 'points_desc') {
						return bValue - aValue; // High to Low
					} else {
						return aValue - bValue; // Low to High
					}
				} else if (this.sortBy === 'fullName' || this.sortBy === 'countryTitle') {
					aValue = a[this.sortBy] ? a[this.sortBy].toLowerCase() : '';
					bValue = b[this.sortBy] ? b[this.sortBy].toLowerCase() : '';
					
					if (this.sortDirection === 'asc') {
						return aValue > bValue ? 1 : -1;
					} else {
						return aValue < bValue ? 1 : -1;
					}
				} else {
					// Legacy support for other sort options
					aValue = a[this.sortBy];
					bValue = b[this.sortBy];
					
					if (this.sortBy === 'position' || this.sortBy === 'scoredPoints') {
						aValue = Number(aValue) || 0;
						bValue = Number(bValue) || 0;
					}
					
					if (this.sortDirection === 'asc') {
						return aValue > bValue ? 1 : -1;
					} else {
						return aValue < bValue ? 1 : -1;
					}
				}
			});

			return filtered;
		},
		
		// Pagination calculations
		filteredTotal() {
			return this.filteredStudents ? this.filteredStudents.length : 0;
		},
		
		totalPages() {
			return Math.ceil(this.filteredTotal / this.itemsPerPage);
		},
		
		startIndex() {
			return (this.currentPage - 1) * this.itemsPerPage;
		},
		
		endIndex() {
			return Math.min(this.startIndex + this.itemsPerPage, this.filteredTotal);
		},
		
		paginatedStudents() {
			return this.filteredStudents.slice(this.startIndex, this.endIndex);
		},
		
		// Smart pagination with ellipsis
		visiblePages() {
			const pages = [];
			const total = this.totalPages;
			const current = this.currentPage;
			
			if (total <= 7) {
				// Show all pages if 7 or fewer
				for (let i = 1; i <= total; i++) {
					pages.push(i);
				}
			} else {
				// Always show first page
				pages.push(1);
				
				if (current <= 4) {
					// Near the beginning
					for (let i = 2; i <= 5; i++) {
						pages.push(i);
					}
					pages.push('...');
					pages.push(total);
				} else if (current >= total - 3) {
					// Near the end
					pages.push('...');
					for (let i = total - 4; i <= total; i++) {
						pages.push(i);
					}
				} else {
					// In the middle
					pages.push('...');
					for (let i = current - 1; i <= current + 1; i++) {
						pages.push(i);
					}
					pages.push('...');
					pages.push(total);
				}
			}
			
			return pages;
		}
	},
	
	watch: {
		listAllStudents	: {
			handler(newVal) {
				if (newVal && Array.isArray(newVal)) {
					this.allStudentsList = newVal;
					this.totalStudents = newVal.length;
				}
			},
			immediate: true
		},
		currentCourse	: {
			handler() {
				if (this.currentCourse && this.currentCourse.id != this.currentCourseId) {
					this.currentCourseId = this.currentCourse.id;
					
					this.fetchList({
						id: this.currentCourse.id
					})
				}
			},
			deep: true,
		},
	},
	
	methods: {
		...mapActions({
			fetchList: 'coursesWidgets/FETCH_STUDENTS_LIST'
		}),
		
		imgAvatar(item) {
			if (item && item.avatar && item.avatar !== '') {
				return item.avatar;
			}
			
			return DefaultAvatar;
		},
		
		formatPoints(points) {
			if (!points) return '0';
			
			return new Intl.NumberFormat().format(Number(points));
		},
		
		getPositionChangeLabel(change) {
			if (change > 0) {
				return this.getTranslation('positionUp', { change });
			} else if (change < 0) {
				return this.getTranslation('positionDown', { change: Math.abs(change) });
			}
			
			return '';
		},
		
		getTranslation(key, params = {}) {
			// Get current locale from multiple sources
			const currentLocale = this.$i18n?.locale || 'en';
			const urlLocale = window.location.pathname.split('/')[1];
			
			const isUkrainian = currentLocale === 'uk' || 
								currentLocale === 'ua' || 
								currentLocale.startsWith('uk') || 
							   urlLocale === 'uk' || 
							   urlLocale === 'ua' || 
							   urlLocale.startsWith('uk');
			
			const locale = isUkrainian ? 'uk' : 'en';
			
			console.log('Translation detection:', {
				key,
				currentLocale,
				urlLocale,
				isUkrainian,
				locale,
				pathname: window.location.pathname
			});
			
			// For Ukrainian locale, prioritize local translations
			if (isUkrainian) {
				const localTranslation = getStudentsTranslation(key, locale, params);
				
				console.log('Ukrainian translation result:', {
					key,
					localTranslation
				});
				
				return localTranslation;
			}
			
			// For English, try server translation first
			try {
				const serverTranslation = this.$t(key, params);
				
				if (serverTranslation && serverTranslation !== key && serverTranslation !== `[${key}]`) {
					return serverTranslation;
				}
			} catch (error) {
				console.warn('Server translation failed for key:', key, error);
			}
			
			// Fallback to local translation
			const localTranslation = getStudentsTranslation(key, locale, params);
			
			console.log('English translation result:', {
				key,
				currentLocale,
				isUkrainian,
				locale,
				serverTranslation: this.$t(key, params),
				localTranslation,
				finalResult: localTranslation
			});
			
			return localTranslation;
		},
		
		handleSearch() {
			if (this.debouncedSearch) {
				this.debouncedSearch();
			}
		},
		
		performSearch() {
			this.currentPage = 1; // Reset to first page when searching
		},
		
		clearSearch() {
			this.searchQuery = '';
			this.currentPage = 1;
		},
		
		goToPage(page) {
			if (page && page <= this.totalPages && page !== this.currentPage) {
				this.currentPage = page;
			}
		},
		
		handleSortChange() {
			this.currentPage = 1; // Reset to first page when sorting changes
		},
		
		handlePerPageChange() {
			this.currentPage = 1; // Reset to first page when items per page changes
		},
		
		toggleSortDropdown() {
			this.isSortDropdownOpen = !this.isSortDropdownOpen;
		},
		
		selectSortOption(value) {
			this.sortBy = value;
			this.isSortDropdownOpen = false;
			this.handleSortChange();
		},
		
		getCurrentSortLabel() {
			const option = this.sortOptions.find(opt => opt.value === this.sortBy);
			return option ? option.label : 'Бали (високі → низькі)';
		}
	}
}
